import React, { useState, useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const EmailModal = ({
  isOpen,
  onClose,
  onSend,
  selectedFile,
  client,
  emailStatus,
}) => {
  const [customMessage, setCustomMessage] = useState("");

  // Set default message when selectedFile becomes available
  useEffect(() => {
    if (selectedFile && isOpen) {
      const fileName = selectedFile.path?.split("/").pop() || "your file";
      setCustomMessage(`<p>Please find attached your requested file: ${fileName}</p>`);
    }
  }, [selectedFile, isOpen]);

  const handleSend = () => {
    onSend(customMessage);
  };

  const handleCancel = () => {
    const fileName = selectedFile?.path?.split("/").pop() || "your file";
    setCustomMessage(`<p>Please find attached your requested file: ${fileName}</p>`);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500 bg-opacity-75">
      <div className="bg-white rounded-sm shadow-xl w-full max-w-2xl p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Send File via Email
        </h2>

        <div className="space-y-4">
          {/* Email Details */}
          <div className="grid grid-cols-2 gap-4 text-sm text-gray-700">
            <div>
              <span className="block font-medium mb-1.5 text-gray-900">From</span>
              <div className="bg-gray-50 px-3 py-2 rounded-sm border border-gray-200">milton@hearsay.ca</div>
            </div>

            <div>
              <span className="block font-medium mb-1.5 text-gray-900">To</span>
              <div className="bg-gray-50 px-3 py-2 rounded-sm border border-gray-200">
                {client?.email}
              </div>
            </div>
          </div>

          <div>
            <span className="block font-medium mb-1.5 text-gray-900">Subject</span>
            <div className="bg-gray-50 px-3 py-2 rounded-sm border border-gray-200 text-sm text-gray-700">
              {`Your requested file: ${selectedFile?.path?.split("/").pop()}`}
            </div>
          </div>

          <div>
            <span className="block font-medium mb-1.5 text-gray-900">Attachment</span>
            <div className="bg-gray-50 px-3 py-2 rounded-sm border border-gray-200 flex items-center text-sm text-gray-700">
              <svg
                className="w-4 h-4 text-gray-400 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z"
                  clipRule="evenodd"
                />
              </svg>
              {selectedFile?.path?.split("/").pop()}
            </div>
          </div>

          {/* Rich Text Message Editor */}
          <div>
            <span className="block font-medium mb-2 text-gray-900">Message</span>
            <div className="border border-gray-300 rounded-sm">
              <ReactQuill
                value={customMessage}
                onChange={setCustomMessage}
                theme="snow"
              />
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Note: The standard HearSay email signature will be automatically added to your message.
            </p>
          </div>

          {/* Status Messages */}
          <div className="mt-6">
            {emailStatus === "sending" && (
              <div className="rounded-sm border border-blue-200 bg-blue-50 p-3">
                <p className="text-blue-700 text-sm flex items-center">
                  <span className="animate-spin mr-2 h-4 w-4 border-2 border-current border-t-transparent rounded-full"></span>
                  Sending...
                </p>
              </div>
            )}
            {emailStatus === "success" && (
              <div className="rounded-sm border border-green-200 bg-green-50 p-3">
                <p className="text-green-700 text-sm flex items-center">
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Email sent successfully!
                </p>
              </div>
            )}
            {emailStatus === "error" && (
              <div className="rounded-sm border border-red-200 bg-red-50 p-3">
                <p className="text-red-700 text-sm flex items-center">
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  Failed to send. Please try again.
                </p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={handleCancel}
              className="inline-flex items-center rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background bg-gray-100 text-gray-900 hover:bg-gray-200 h-9 px-4 py-2 shadow-sm"
              disabled={emailStatus === "sending"}>
              Cancel
            </button>
            <button
              onClick={handleSend}
              className="inline-flex items-center rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background bg-gray-900 text-white hover:bg-gray-800 h-9 px-4 py-2 shadow-sm"
              disabled={emailStatus === "sending" || emailStatus === "success"}>
              Send Email
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailModal;